import React from 'react';
import { Button } from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';
import useDeviceEvents from '../hooks/useDeviceEvents';

/**
 * Keypad button component for numeric input interfaces
 * Used for touch-friendly input in forms and authentication screens
 *
 * @param {*} props Component props
 * @param {number|string} props.value Button value ('backspace' or 'reset' for special functions)
 * @param {function} props.onClick Click handler function
 * @param {number} props.width Button width multiplier (default: 1)
 * @param {boolean} props.disabled Whether the button is disabled
 * @param {ReactNode} props.icon Custom icon to override default icon
 * @returns Keypad button component
 */
const KeypadButton = ({ value, onClick, width = 1, disabled = false, icon = null }) => {
  // Render the appropriate content based on value
  const renderButtonContent = () => {
    if (icon) {
      return icon;
    } else if (value === 'backspace') {
      return <BackspaceIcon />;
    } else if (value === 'reset') {
      return <RefreshIcon />;
    } else if (value === 'enter') {
      return <LoginIcon />;
    } else {
      return value;
    }
  };

  const eventHandlers = useDeviceEvents((e) => {
    e.preventDefault();
    onClick(value);
  }, { disabled });

  return (
    <Button
      variant="outlined"
      {...eventHandlers}
      disabled={disabled}
      sx={{
        fontSize: value === 'backspace' || value === 'reset' ? '1.5rem' : '2rem',
        width: `100%`,
        height: `100%`,
        borderRadius: 0,
        fontWeight: 'medium',
        borderTopWidth: 2,
        borderLeftWidth: 2,
        borderBottomWidth: 0,
        borderRightWidth: 0,
      }}
    >
      {renderButtonContent()}
    </Button>
  );
};

export default KeypadButton;